import { ActionTree, Dispatch, GetterTree, Module, MutationTree } from 'vuex';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  IInvoice,
  IInvoiceQuote,
  IPagination,
  IResponse,
  RootState,
} from '@/types/types';
import { isNetworkError } from '@/utils/helpers';
import {
  cancelInvoice,
  issueInvoice,
  invoiceDetails,
  listInvoices,
  payInvoice,
  quoteInvoice,
} from '@/services/invoices.service';

interface InvoicesState {
  list: ReadonlyArray<IInvoice>;
  details: IInvoice | null;
  quote: IInvoiceQuote | null;
  paginate: { page: number; pages: number; total: number; limit: number };
  [key: string]: unknown;
}

const state: InvoicesState = {
  list: [],
  details: null,
  quote: null,
  paginate: { page: 1, pages: 1, total: 0, limit: 15 },
};

const mutations: MutationTree<InvoicesState> = {
  UPDATE_LIST(state, payload: IInvoice[]) {
    state.list = payload;
  },
  UPDATE_DETAILS(state, payload: IInvoice | null) {
    state.details = payload;
  },
  UPDATE_QUOTE(state, payload: IInvoiceQuote | null) {
    state.quote = payload;
  },
  UPDATE_PAGINATION(state, payload: InvoicesState['paginate']) {
    state.paginate = payload;
  },
};

/** every action reports failures the same way, so keep it in one place */
const reportError = (dispatch: Dispatch, e: any): void => {
  const message = isNetworkError(e)
    ? e?.message
    : e?.response?.data?.message ?? 'Something went wrong';
  dispatch('snackBarMessage', message, { root: true });
  dispatch('snackBarVisibility', true, { root: true });
};

const actions: ActionTree<InvoicesState, RootState> = {
  async list({ commit, dispatch }, payload) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const query = payload?.query ?? '?page=1&limit=15';
      const response$ = of<IResponse<IPagination<IInvoice>>>(
        await listInvoices(query)
      ).pipe(map((response) => response.data?.paginateObj));
      response$.subscribe((paginateObj) => {
        commit('UPDATE_LIST', paginateObj?.docs ?? []);
        commit('UPDATE_PAGINATION', {
          page: paginateObj?.page ?? 1,
          pages: paginateObj?.pages ?? 1,
          total: paginateObj?.total ?? 0,
          limit: paginateObj?.limit ?? 15,
        });
        dispatch('isPageLoading', false, { root: true });
      });
    } catch (e) {
      dispatch('isPageLoading', false, { root: true });
      reportError(dispatch, e);
    }
  },

  async details({ commit, dispatch }, id: number) {
    try {
      dispatch('isDialogLoading', true, { root: true });
      const response$ = of<IResponse<IInvoice>>(await invoiceDetails(id)).pipe(
        map((response) => response.data)
      );
      /* returned as well as committed: callers hand off on the invoice
         (prefilling the momo flow), and a void action left them with
         undefined and no way to tell success from failure */
      let fetched: IInvoice | null = null;
      response$.subscribe((doc) => {
        fetched = doc;
        commit('UPDATE_DETAILS', doc);
        dispatch('isDialogLoading', false, { root: true });
      });
      return fetched;
    } catch (e) {
      dispatch('isDialogLoading', false, { root: true });
      reportError(dispatch, e);
      return null;
    }
  },

  /** live pricing while the customer types an amount */
  async quote({ commit, dispatch }, amount: number) {
    try {
      if (!amount || Number(amount) <= 0) {
        commit('UPDATE_QUOTE', null);
        return;
      }
      const response$ = of<IResponse<IInvoiceQuote>>(
        await quoteInvoice(Number(amount))
      ).pipe(map((response) => response.data));
      response$.subscribe((doc) => commit('UPDATE_QUOTE', doc));
    } catch (e) {
      /* a quote failing is not worth a toast — the amount may just sit
         outside the configured plan bands while the user is still typing */
      commit('UPDATE_QUOTE', null);
    }
  },

  async issue({ commit, dispatch }, payload) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<IResponse<IInvoice>>(
        await issueInvoice(payload)
      ).pipe(map((response) => response.data));
      let issued: IInvoice | null = null;
      response$.subscribe((doc) => {
        issued = doc;
        commit('UPDATE_DETAILS', doc);
        dispatch('snackBarMessage', `Invoice ${doc?.invoiceNumber} issued`, {
          root: true,
        });
        dispatch('snackBarVisibility', true, { root: true });
        dispatch('isLoading', false, { root: true });
      });
      return issued;
    } catch (e) {
      dispatch('isLoading', false, { root: true });
      reportError(dispatch, e);
      return null;
    }
  },

  async pay({ dispatch }, { id, body }) {
    try {
      dispatch('isLoading', true, { root: true });
      const response = await payInvoice<IResponse<unknown>>(id, body ?? {});
      dispatch('isLoading', false, { root: true });
      return response?.data ?? true;
    } catch (e) {
      dispatch('isLoading', false, { root: true });
      reportError(dispatch, e);
      return null;
    }
  },

  async cancel({ commit, dispatch }, id: number) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<IResponse<IInvoice>>(await cancelInvoice(id)).pipe(
        map((response) => response.data)
      );
      response$.subscribe((doc) => {
        commit('UPDATE_DETAILS', doc);
        dispatch('snackBarMessage', 'Invoice cancelled', { root: true });
        dispatch('snackBarVisibility', true, { root: true });
        dispatch('isLoading', false, { root: true });
      });
    } catch (e) {
      dispatch('isLoading', false, { root: true });
      reportError(dispatch, e);
    }
  },
};

const getters: GetterTree<InvoicesState, RootState> = {
  getInvoices: (state) => state.list,
  getInvoiceDetails: (state) => state.details,
  getInvoiceQuote: (state) => state.quote,
  getPagination: (state) => state.paginate,
};

export const invoices: Module<InvoicesState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
